import { z } from 'zod';
import { i18nTextSchema, moneySchema } from './common.js';

/**
 * Contracts for the F&B and services verticals (CLAUDE.md §4).
 *
 * §4's rule is that verticals differ in four places and no more: catalog
 * extension tables, checkout flow variant, UI presets, and which layout enums
 * are allowed. This file is the first of those, expressed as schemas — there
 * is no second storefront and no second admin.
 */

/* ------------------------------------------------------------------ */
/* F&B — modifiers                                                     */
/* ------------------------------------------------------------------ */

export const modifierSelectionSchema = z.enum(['single', 'multiple']);

export const upsertModifierOptionSchema = z.object({
  id: z.string().uuid().optional(),
  name: i18nTextSchema,
  /** Signed: a smaller portion is legitimately negative. */
  priceDelta: z.number().int().min(-100_000).max(100_000).default(0),
  isDefault: z.boolean().default(false),
  isAvailable: z.boolean().default(true),
  position: z.number().int().min(0).default(0),
});

export const upsertModifierGroupSchema = z
  .object({
    name: i18nTextSchema,
    selection: modifierSelectionSchema.default('single'),
    minSelect: z.number().int().min(0).max(20).default(1),
    maxSelect: z.number().int().min(1).max(20).default(1),
    position: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
    options: z.array(upsertModifierOptionSchema).min(1).max(30),
  })
  .refine((group) => group.maxSelect >= group.minSelect, {
    message: 'maxSelect cannot be lower than minSelect',
  })
  .refine((group) => group.selection !== 'single' || group.maxSelect === 1, {
    message: 'A single-select group can only allow one choice',
  })
  .refine((group) => group.options.filter((option) => option.isDefault).length <= group.maxSelect, {
    message: 'More options are marked default than the group allows',
  });
export type UpsertModifierGroupInput = z.infer<typeof upsertModifierGroupSchema>;

export const modifierGroupSchema = z.object({
  id: z.string().uuid(),
  name: i18nTextSchema,
  selection: modifierSelectionSchema,
  minSelect: z.number().int(),
  maxSelect: z.number().int(),
  position: z.number().int(),
  isActive: z.boolean(),
  options: z.array(
    z.object({
      id: z.string().uuid(),
      name: i18nTextSchema,
      priceDelta: z.number().int(),
      isDefault: z.boolean(),
      isAvailable: z.boolean(),
      position: z.number().int(),
    }),
  ),
});
export type ModifierGroupDto = z.infer<typeof modifierGroupSchema>;

/** What a customer picked: group id → option ids. */
export const modifierSelectionInputSchema = z
  .record(z.string().uuid(), z.array(z.string().uuid()).max(20))
  .default({});
export type ModifierSelectionInput = z.infer<typeof modifierSelectionInputSchema>;

/* ------------------------------------------------------------------ */
/* F&B — the item                                                      */
/* ------------------------------------------------------------------ */

export const sellingHoursSchema = z
  .array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      open: z.string().regex(/^\d{2}:\d{2}$/),
      close: z.string().regex(/^\d{2}:\d{2}$/),
    }),
  )
  .max(21)
  .default([]);

export const upsertFnbItemSchema = z.object({
  /** §4's one-tap sold-out toggle, kept apart from publish/unpublish. */
  isSoldOut: z.boolean().default(false),
  sellingHours: sellingHoursSchema,
  prepMinutes: z.number().int().min(0).max(240).default(15),
  modifierGroupIds: z.array(z.string().uuid()).max(20).default([]),
});
export type UpsertFnbItemInput = z.infer<typeof upsertFnbItemSchema>;

/**
 * How an F&B order reaches the customer.
 *
 * §4 rules out provincial shipping and COD remittance for this vertical: the
 * food travels a few kilometres at most, and the courier is the shop's own
 * moto rather than a national carrier with a payout batch.
 */
export const fulfilmentMethodSchema = z.enum(['pickup', 'local_delivery', 'dine_in']);
export type FulfilmentMethod = z.infer<typeof fulfilmentMethodSchema>;

export const fnbSettingsSchema = z.object({
  pickupEnabled: z.boolean().default(true),
  localDeliveryEnabled: z.boolean().default(false),
  dineInEnabled: z.boolean().default(false),
  /** Flat fee inside the delivery radius; §4 gives F&B no province table. */
  localDeliveryFee: z.number().int().min(0).default(0),
  localDeliveryMinimum: z.number().int().min(0).default(0),
  /** Minutes between offered pickup times. */
  slotIntervalMinutes: z.number().int().min(5).max(120).default(15),
  /** How far ahead a customer may order. */
  orderAheadDays: z.number().int().min(0).max(14).default(1),
});
export type FnbSettings = z.infer<typeof fnbSettingsSchema>;

/* ------------------------------------------------------------------ */
/* Services — resources and booking                                    */
/* ------------------------------------------------------------------ */

export const workingHoursSchema = z
  .array(
    z.object({
      weekday: z.number().int().min(0).max(6),
      open: z.string().regex(/^\d{2}:\d{2}$/),
      close: z.string().regex(/^\d{2}:\d{2}$/),
    }),
  )
  .max(21)
  .default([]);

export const upsertResourceSchema = z.object({
  name: z.string().min(1).max(80),
  /**
   * §4: staff in `identity-team` ARE the bookable resources. Null covers the
   * things that are bookable but have no login — a treatment room, a table.
   */
  userId: z.string().uuid().nullable().optional(),
  photoUrl: z.string().url().nullable().optional(),
  isActive: z.boolean().default(true),
  position: z.number().int().min(0).default(0),
  hours: workingHoursSchema,
});
export type UpsertResourceInput = z.infer<typeof upsertResourceSchema>;

export const resourceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  userId: z.string().uuid().nullable(),
  photoUrl: z.string().nullable(),
  isActive: z.boolean(),
  position: z.number().int(),
  hours: workingHoursSchema,
});
export type ResourceDto = z.infer<typeof resourceSchema>;

export const upsertServiceSchema = z.object({
  durationMinutes: z.number().int().min(5).max(600).default(60),
  bufferMinutes: z.number().int().min(0).max(240).default(0),
  /** Null books on trust; a number holds the slot against a deposit (§4). */
  depositAmount: z.number().int().min(0).nullable().default(null),
  /** Empty means any active resource can perform it. */
  resourceIds: z.array(z.string().uuid()).max(50).default([]),
});
export type UpsertServiceInput = z.infer<typeof upsertServiceSchema>;

export const slotQuerySchema = z.object({
  productId: z.string().uuid(),
  /** `YYYY-MM-DD` in Phnom Penh terms (§6.5 — one timezone, no offsets). */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  resourceId: z.string().uuid().optional(),
});

export const slotSchema = z.object({
  startsAt: z.string().datetime(),
  serviceEndsAt: z.string().datetime(),
  resourceId: z.string().uuid(),
  resourceName: z.string(),
});
export type SlotDto = z.infer<typeof slotSchema>;

export const slotDaySchema = z.object({
  date: z.string(),
  slots: z.array(slotSchema),
  /** Set when the day yields nothing, so the UI can say why. */
  closedReason: z.enum(['no_hours', 'fully_booked', 'past']).nullable().default(null),
});
export type SlotDayDto = z.infer<typeof slotDaySchema>;

/** Picking a slot at checkout. */
export const bookingRequestSchema = z.object({
  productId: z.string().uuid(),
  resourceId: z.string().uuid(),
  startsAt: z.string().datetime(),
  note: z.string().max(500).optional(),
});
export type BookingRequestInput = z.infer<typeof bookingRequestSchema>;

export const rescheduleSchema = z.object({
  orderId: z.string().uuid(),
  resourceId: z.string().uuid(),
  startsAt: z.string().datetime(),
});

export const appointmentSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid().nullable(),
  orderCode: z.string().nullable(),
  resourceId: z.string().uuid(),
  resourceName: z.string(),
  serviceName: z.string().nullable(),
  customerName: z.string().nullable(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  kind: z.enum(['booking', 'block']),
  status: z.string().nullable(),
  note: z.string().nullable(),
});
export type AppointmentDto = z.infer<typeof appointmentSchema>;

export const blockTimeSchema = z.object({
  resourceId: z.string().uuid(),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  note: z.string().max(200).optional(),
});

/**
 * §4: "auto-release after 30 min unpaid". A held slot that nobody pays for
 * must go back on sale, or one abandoned checkout costs the shop a Saturday
 * afternoon.
 */
export const DEPOSIT_HOLD_MINUTES = 30;

/** §4 reminders at 24h and 2h, each with inline Reschedule / Cancel. */
export const BOOKING_REMINDER_HOURS = [24, 2] as const;

/** Slots closer than this are not offered — the customer has to travel. */
export const DEFAULT_BOOKING_NOTICE_MINUTES = 60;
